import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { LoginViewModule } from './modules/login-view/login-view.module';
import { RouterModule } from '@angular/router';
import { AuthModule } from './core/auth/auth.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CollectionsModule } from './collections/collections.module';
import { Store, StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { MojoAvatarModule } from 'mojo-avatar';
import { SnackbarModule } from './shared/snackbar/snackbar.module';
import {
  AVATAR_CONFIG_SERVICE,
  AVATAR_DOWNLOAD_SERVICE,
  AvatarDownloadService,
} from './core/injection-tokens/avatar-injection-tokens';
import {
  FEATURE_ACCESS_API_URL,
  featureAccessAreReady,
  featureAccessIsActive,
  loadFeatureAccess,
  MojoFeatureAccessModule,
} from 'mojo-feature-access';
import { environment } from '../environments/environment';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { MojoExternalCandidateContactInfoModule } from 'mojo-external-candidate-contact-info';
import { EXTERNAL_CANDIDATE_CONTACT_INFO_POOLS_SERVICE } from './core/injection-tokens/external-candidate-contact-info';
import { ContactService } from './shared/services/contact-service';
import { EDITOR_SERVICE } from './core/injection-tokens/editor.injection.token';
import { ATTACHMENT_SERVICE } from './core/injection-tokens/attachments.injection.token';
import { AttachmentService } from './shared/services/attachment.service';
import { EditorService } from './shared/services/editor.service';
import { KeycloakAngularModule } from 'keycloak-angular';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AuthService } from '@app/core/auth/services/auth.service';
import { filter, first, firstValueFrom } from 'rxjs';
import { TASKS_SERVICE } from './core/injection-tokens/tasks.injection.token';
import { TasksService } from './shared/services/tasks.service';
import { NOTES_SERVICE } from './core/injection-tokens/note.injection';
import { NotesService } from './shared/services/notes.service';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { TALENT_POOLS_SERVICE } from './core/injection-tokens/talent-pools.injection.token';
import { TalentPoolsService } from './shared/services/talent-pools.service';
import { loadUserProfile, loadUserSubscriptionType } from '@app/collections/users/users.actions';
import {
  getIsUserSubscriptionTypeLoaded,
  getLoggedInUser,
} from '@app/collections/users/users.selectors';
import { FastrApiInterceptor } from './core/interceptors/fastr-api.interceptor';
import { AuthTokenInterceptor } from './core/interceptors/auth-token.interceptor';
import { provideAuthProvider } from './core/auth/providers/provide-auth-provider';
import { FEATURE_FLAG_API_URL, featureFlagsLoadAll, FeatureFlagsModule } from 'mojo-feature-flags';
import { GLOBAL_PRIVILEGES } from './core/injection-tokens/global-privileges.injection-token';
import { MojoPrivilegesModule, MojoPrivilegesService } from 'mojo-privileges';
import { FeatureAccessConfig } from './shared/config/feature-access.config';

function registerSvgIcons(iconRegistry: MatIconRegistry, sanitizer: DomSanitizer) {
  return () => {
    iconRegistry.addSvgIcon(
      'ai_pencil',
      sanitizer.bypassSecurityTrustResourceUrl('assets/icons/ai-write.svg'),
    );
    iconRegistry.addSvgIcon(
      'ai_edit',
      sanitizer.bypassSecurityTrustResourceUrl('assets/icons/icon-ai.svg'),
    );
  };
}

export function loadLoggedInUserFactory(store: Store, authService: AuthService) {
  return async () => {
    await firstValueFrom(
      authService.isUserAuthorized$.pipe(
        filter((isAuthorized) => isAuthorized),
        first(),
      ),
    );
    store.dispatch(loadUserProfile());
    await firstValueFrom(store.select(getLoggedInUser).pipe(filter((user) => !!user)));
    store.dispatch(loadFeatureAccess());
    await firstValueFrom(store.select(featureAccessAreReady).pipe(filter((ready) => !!ready)));
    const isVanguardRecruiter = await firstValueFrom(
      store.select(featureAccessIsActive(FeatureAccessConfig.VANGUARD_RECRUITER)),
    );
    if (isVanguardRecruiter) {
      store.dispatch(featureFlagsLoadAll());
      return;
    }
    if (!(await firstValueFrom(store.select(featureAccessIsActive(FeatureAccessConfig.PRO))))) {
      return;
    }
    store.dispatch(loadUserSubscriptionType());
    await firstValueFrom(store.select(getIsUserSubscriptionTypeLoaded).pipe(filter(Boolean)));
    store.dispatch(featureFlagsLoadAll());
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    MatSnackBarModule,
    AppRoutingModule,
    LoginViewModule,
    RouterModule,
    AuthModule.forRoot(),
    BrowserAnimationsModule,
    StoreModule.forRoot([]),
    EffectsModule.forRoot([]),
    StoreDevtoolsModule.instrument({
      name: 'Fastr.ai',
      features: { test: true },
      logOnly: false,
    }),
    SnackbarModule,
    CollectionsModule,
    MojoAvatarModule.forRoot(AVATAR_DOWNLOAD_SERVICE, AVATAR_CONFIG_SERVICE),
    MojoExternalCandidateContactInfoModule.forRoot(EXTERNAL_CANDIDATE_CONTACT_INFO_POOLS_SERVICE),
    KeycloakAngularModule,
    MojoFeatureAccessModule,
    FeatureFlagsModule,
    MojoPrivilegesModule.forRoot(GLOBAL_PRIVILEGES),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    { provide: GLOBAL_PRIVILEGES, useClass: MojoPrivilegesService },
    ...provideAuthProvider(),
    {
      provide: APP_INITIALIZER,
      useFactory: loadLoggedInUserFactory,
      deps: [Store, AuthService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: registerSvgIcons,
      deps: [MatIconRegistry, DomSanitizer],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FastrApiInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthTokenInterceptor,
      multi: true,
    },
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json',
      }),
      lang: 'en',
      fallbackLang: 'en',
    }),
    {
      provide: AVATAR_DOWNLOAD_SERVICE,
      useClass: AvatarDownloadService,
    },
    {
      provide: AVATAR_CONFIG_SERVICE,
      useValue: {},
    },
    {
      provide: EXTERNAL_CANDIDATE_CONTACT_INFO_POOLS_SERVICE,
      useClass: ContactService,
    },
    {
      provide: FEATURE_ACCESS_API_URL,
      useValue: environment.API_BASE_URL + '/licenses',
    },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    { provide: EDITOR_SERVICE, useClass: EditorService },
    { provide: ATTACHMENT_SERVICE, useClass: AttachmentService },
    { provide: TASKS_SERVICE, useClass: TasksService },
    { provide: NOTES_SERVICE, useClass: NotesService },
    { provide: TALENT_POOLS_SERVICE, useClass: TalentPoolsService },
    {
      provide: FEATURE_FLAG_API_URL,
      useValue: environment.API_BASE_URL,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
